/**
 * One way in: a server-side session (ADR-005).
 *
 * The browser holds an httpOnly cookie it cannot read and no token at all, so
 * this provider's whole job is to ask the server two anonymous questions at
 * boot — which mode are you in, and do you know me — and then to route the
 * three actions (sign in, sign out, and in dev mode pick a seeded user) back
 * to the server.
 *
 * SALVAGE §4 D1: msal, the bearer token and the token provider are gone. What
 * is left is a redirect and a cookie.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch, setAuthMode, setDevActor } from '@/api/client';
import { endpoints } from '@/api/endpoints';
import {
  DEFAULT_AUTH_CONFIG,
  DEV_USER_STORAGE_KEY,
  loginHref,
  readAuthConfig,
  type AuthConfig,
  type AuthMode,
} from './config';
import { resolveSessionAuth } from './state';

interface AuthContextValue {
  mode: AuthMode;
  /** Both boot probes have answered. */
  ready: boolean;
  /** The server accepts this browser as somebody. */
  signedIn: boolean;
  /**
   * Whether the last request could carry a credential. With a cookie the
   * answer is "always, once we know there is a session" — kept so the shell's
   * "a 401 WITH a credential is a real refusal" distinction still holds.
   */
  credentialAttached: boolean;
  actorLabel: string | null;
  signIn: () => void;
  signOut: () => void;
  /** dev mode only */
  setDevUser: (key: string | null) => void;
  /** What the server says is misconfigured; empty on a healthy instance. */
  problems: string[];
}

const AuthContext = createContext<AuthContextValue>({
  mode: 'entra',
  ready: false,
  signedIn: false,
  credentialAttached: false,
  actorLabel: null,
  signIn: () => {},
  signOut: () => {},
  setDevUser: () => {},
  problems: [],
});

export function useAuth() {
  return useContext(AuthContext);
}

function readStoredDevUser(): string | null {
  try {
    return localStorage.getItem(DEV_USER_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** The config probe is anonymous and must answer even on a broken instance. */
async function fetchAuthConfig(): Promise<AuthConfig> {
  try {
    const res = await fetch('/api/auth/config-check', { headers: { accept: 'application/json' } });
    return readAuthConfig(await res.json());
  } catch {
    return DEFAULT_AUTH_CONFIG;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [session, setSession] = useState<{ resolved: boolean; label: string | null }>({
    resolved: false,
    label: null,
  });
  const [devUser, setDevUserState] = useState<string | null>(() => readStoredDevUser());

  // Probe 1: what is the server configured for? Runs once, before anything
  // else can decide whether to show a sign-in screen.
  useEffect(() => {
    let cancelled = false;
    void fetchAuthConfig().then((c) => {
      if (cancelled) return;
      setAuthMode(c.authMode);
      setConfig(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Probe 2: does the server know this browser? In dev mode the stored seeded
  // user is the credential, so the probe re-runs when it changes.
  useEffect(() => {
    if (!config) return;
    if (config.authMode === 'dev') setDevActor(devUser);
    let cancelled = false;
    void apiFetch<{ displayName?: string }>(endpoints.me)
      .then((me) => {
        if (!cancelled) setSession({ resolved: true, label: me.displayName ?? null });
      })
      .catch(() => {
        if (!cancelled) setSession({ resolved: true, label: null });
      });
    return () => {
      cancelled = true;
    };
  }, [config, devUser]);

  const setDevUser = useCallback(
    (key: string | null) => {
      try {
        if (key) localStorage.setItem(DEV_USER_STORAGE_KEY, key);
        else localStorage.removeItem(DEV_USER_STORAGE_KEY);
      } catch {
        /* storage blocked — the choice lives for this page load only */
      }
      setDevActor(key);
      setSession({ resolved: false, label: null });
      setDevUserState(key);
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const mode = config?.authMode ?? 'entra';
  const { ready, signedIn } = resolveSessionAuth({
    configResolved: config !== null,
    sessionResolved: session.resolved,
    hasSession: session.label !== null,
  });

  const value = useMemo<AuthContextValue>(
    () => ({
      mode,
      ready,
      signedIn,
      // The cookie rides on every same-origin request, so once the session is
      // resolved there is no window in which a request goes out bare.
      credentialAttached: mode === 'dev' ? Boolean(devUser) : session.resolved,
      actorLabel: session.label ?? (mode === 'dev' ? devUser : null),
      signIn: () => {
        window.location.assign(loginHref(window.location.pathname + window.location.search));
      },
      signOut: () => {
        void apiFetch(endpoints.logout, { method: 'POST' })
          .catch(() => undefined)
          .then(() => {
            if (mode === 'dev') setDevUser(null);
            else window.location.assign('/');
          });
      },
      setDevUser,
      problems: config?.problems ?? [],
    }),
    [config, devUser, mode, ready, session.label, session.resolved, setDevUser, signedIn],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
